import clsx from 'clsx';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ToastProps } from './types';

import { getStatusIcon } from '../../utils/getStatusIcon';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import './Toast.css';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import { getToastRole } from './utils';

const messages = defineMessages({
  dismiss: {
    id: 'Toast.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Accessible name for the toast dismiss button',
  },
  statusCautious: {
    id: 'Toast.statusCautious',
    defaultMessage: 'Caution',
    description: 'Accessible name for a cautious toast status icon',
  },
  statusCritical: {
    id: 'Toast.statusCritical',
    defaultMessage: 'Critical',
    description: 'Accessible name for a critical toast status icon',
  },
  statusInformational: {
    id: 'Toast.statusInformational',
    defaultMessage: 'Information',
    description: 'Accessible name for an informational toast status icon',
  },
  statusNeutral: {
    id: 'Toast.statusNeutral',
    defaultMessage: 'Notice',
    description: 'Accessible name for a neutral toast status icon',
  },
  statusPositive: {
    id: 'Toast.statusPositive',
    defaultMessage: 'Success',
    description: 'Accessible name for a positive toast status icon',
  },
});

const statusIconLabels = {
  cautious: messages.statusCautious,
  critical: messages.statusCritical,
  informational: messages.statusInformational,
  neutral: messages.statusNeutral,
  positive: messages.statusPositive,
} as const;

export const Toast = memo(
  ({ link, onDismiss, status = 'positive', text, title }: ToastProps) => {
    const intl = useIntl();
    const statusIcon = getStatusIcon(status);
    const dismissLabel = intl.formatMessage(messages.dismiss);

    return (
      <Layout
        align="start"
        aria-atomic="true"
        className={clsx('eds-toast', `eds-toast--status-${status}`)}
        role={getToastRole(status)}
        spacing="s"
      >
        <span className="eds-toast--status-icon">
          <Icon
            icon={statusIcon}
            label={intl.formatMessage(statusIconLabels[status])}
            size="m"
          />
        </span>
        <Layout direction="column" flex="auto" spacing="s">
          <div className="eds-toast--message-block">
            {title ? (
              <Text as="p" variant="body-semi-s">
                {title}
              </Text>
            ) : null}
            <Text as="p" variant="body-s">
              {text}
            </Text>
          </div>
          {link ? (
            <Link
              external={link.external}
              href={link.href}
              size={link.size ?? 's'}
              text={link.text}
            />
          ) : null}
        </Layout>
        {onDismiss ? (
          <IconButton
            icon={{ key: 'x', label: dismissLabel }}
            label={dismissLabel}
            onClick={onDismiss}
            size="s"
            variant="tertiary"
          />
        ) : null}
      </Layout>
    );
  },
);

Toast.displayName = 'Toast';
